import React, { useState, useEffect } from 'react';
import SortableHeader from './SortableHeader';
import { useTableSort } from '../utils/tableSort';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  GetApp as TemplateIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { MaterialLexware, MaterialLexwareCreate, MaterialLexwareUpdate } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from './ToastProvider';
import { apiErrorMessage } from '../utils/apiError';

interface MaterialLexwareTableProps {
  userRole: string;
}

const MaterialLexwareTable: React.FC<MaterialLexwareTableProps> = ({ userRole }) => {
  const [lexwareItems, setLexwareItems] = useState<MaterialLexware[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toastError } = useToast();

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editItem, setEditItem] = useState<MaterialLexware | null>(null);

  // Form states
  const [formMaterialnummer, setFormMaterialnummer] = useState('');
  const [formCoilBreite, setFormCoilBreite] = useState<number>(0);
  const [formCoilDicke, setFormCoilDicke] = useState<number>(0);

  // Excel import/export states
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [uploading, setUploading] = useState(false);
  const [importResults, setImportResults] = useState<string | null>(null);

  const isEuUser = userRole === 'KALOTTE';

  useEffect(() => {
    loadLexwareItems();
  }, []);

  const loadLexwareItems = async () => {
    try {
      setLoading(true);
      const items = await apiService.getMaterialLexwares();
      setLexwareItems(items);
      setError(null);
    } catch (error) {
      console.error('Error loading material lexware items:', error);
      setError('Fehler beim Laden der Material Lexware Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode: 'create' | 'edit', item?: MaterialLexware) => {
    setDialogMode(mode);
    setEditItem(item || null);
    setFormMaterialnummer(item?.materialnummer || '');
    setFormCoilBreite(item?.coil_breite || 0);
    setFormCoilDicke(item?.coil_dicke || 0);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditItem(null);
    setFormMaterialnummer('');
    setFormCoilBreite(0);
    setFormCoilDicke(0);
  };

  const handleSave = async () => {
    try {
      const lexwareData: MaterialLexwareCreate = {
        materialnummer: formMaterialnummer,
        coil_breite: formCoilBreite,
        coil_dicke: formCoilDicke,
      };

      if (dialogMode === 'create') {
        await apiService.createMaterialLexware(lexwareData);
      } else if (editItem) {
        const updateData: MaterialLexwareUpdate = lexwareData;
        await apiService.updateMaterialLexware(editItem.id, updateData);
      }

      handleCloseDialog();
      await loadLexwareItems();
      setError(null);
    } catch (error: any) {
      console.error('Error saving material lexware:', error);
      toastError(apiErrorMessage(error, 'Fehler beim Speichern'));
    }
  };

  const handleDelete = async (item: MaterialLexware) => {
    if (window.confirm(`Soll "${item.materialnummer}" wirklich gelöscht werden?`)) {
      try {
        await apiService.deleteMaterialLexware(item.id);
        await loadLexwareItems();
        setError(null);
      } catch (error: any) {
        console.error('Error deleting material lexware:', error);
        toastError(apiErrorMessage(error, 'Fehler beim Löschen'));
      }
    }
  };

  // Excel Functions
  const handleExportExcel = async () => {
    try {
      setMenuAnchorEl(null);
      const blob = await apiService.exportMaterialLexwareExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'material_lexware.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error exporting Excel:', error);
      toastError(apiErrorMessage(error, 'Fehler beim Excel-Export'));
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setMenuAnchorEl(null);
      const blob = await apiService.downloadMaterialLexwareTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'material_lexware_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading template:', error);
      toastError(apiErrorMessage(error, 'Fehler beim Template-Download'));
    }
  };

  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setMenuAnchorEl(null);
      setError(null);
      setImportResults(null);

      const result = await apiService.importMaterialLexwareExcel(file);

      let message = `Import erfolgreich: ${result.created} erstellt, ${result.updated} aktualisiert`;
      if (result.errors && result.errors.length > 0) {
        message += `\n\nFehler:\n${result.errors.join('\n')}`;
        if (result.additional_errors) {
          message += `\n... und ${result.additional_errors} weitere Fehler`;
        }
      }

      setImportResults(message);
      await loadLexwareItems();
    } catch (error: any) {
      console.error('Error importing Excel:', error);
      toastError(apiErrorMessage(error, 'Fehler beim Excel-Import'));
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const filteredItems = searchTerm
    ? lexwareItems.filter((item) => {
        const term = searchTerm.toLowerCase();
        return (
          item.materialnummer.toLowerCase().includes(term) ||
          String(item.coil_dicke).includes(term) ||
          String(item.coil_breite).includes(term)
        );
      })
    : lexwareItems;
  const { sort, order, onSort, sortRows } = useTableSort<
    'materialnummer' | 'coil_dicke' | 'coil_breite' | 'created_at'
  >('materialnummer', 'asc', ['created_at']);
  const visibleItems = sortRows(filteredItems, {
    materialnummer: (item) => item.materialnummer,
    coil_dicke: (item) => item.coil_dicke,
    coil_breite: (item) => item.coil_breite,
    created_at: (item) => item.created_at,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Material Lexware ({filteredItems.length}
          {searchTerm ? ` / ${lexwareItems.length}` : ''})
        </Typography>
        {isEuUser && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Neuer Eintrag
            </Button>
            <Button
              variant="outlined"
              startIcon={<MoreVertIcon />}
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            >
              Excel
            </Button>
          </Box>
        )}
      </Box>

      <TextField
        size="small"
        placeholder="Suche nach Materialnummer, Dicke, Breite..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 400 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', py: 0.75 } }}>
            <TableHead>
              <TableRow>
                <SortableHeader column="materialnummer" label="Materialnummer" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="coil_dicke" label="Coil Dicke (mm)" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="coil_breite" label="Coil Breite (mm)" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="created_at" label="Erstellt am" sort={sort} order={order} onSort={onSort} />
                {isEuUser && <TableCell align="right">Aktionen</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{item.materialnummer}</TableCell>
                  <TableCell>{item.coil_dicke}</TableCell>
                  <TableCell>{item.coil_breite}</TableCell>
                  <TableCell>{new Date(item.created_at).toLocaleDateString('de-DE')}</TableCell>
                  {isEuUser && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit', item)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create'
            ? 'Neuer Material Lexware Eintrag'
            : 'Material Lexware bearbeiten'}
        </DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            label="Materialnummer"
            value={formMaterialnummer}
            onChange={(e) => setFormMaterialnummer(e.target.value)}
            margin="normal"
          />
          <TextField
            required
            fullWidth
            label="Coil Dicke (mm)"
            type="number"
            value={formCoilDicke}
            onChange={(e) => setFormCoilDicke(Number(e.target.value))}
            margin="normal"
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            required
            fullWidth
            label="Coil Breite (mm)"
            type="number"
            value={formCoilBreite}
            onChange={(e) => setFormCoilBreite(Number(e.target.value))}
            margin="normal"
            inputProps={{ min: 0, step: 0.1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formMaterialnummer || formCoilBreite <= 0 || formCoilDicke <= 0}
          >
            {dialogMode === 'create' ? 'Erstellen' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem onClick={handleExportExcel}>
          <DownloadIcon sx={{ mr: 1 }} />
          Exportieren
        </MenuItem>
        <MenuItem onClick={handleDownloadTemplate}>
          <TemplateIcon sx={{ mr: 1 }} />
          Template herunterladen
        </MenuItem>
        <Divider />
        <MenuItem component="label" disabled={uploading}>
          <UploadIcon sx={{ mr: 1 }} />
          {uploading ? 'Importiere...' : 'Importieren'}
          <input
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
            disabled={uploading}
          />
        </MenuItem>
      </Menu>

      {/* Import Results Alert */}
      {importResults && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setImportResults(null)}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {importResults}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default MaterialLexwareTable;
